<template>
  <div>
    <div id="explanation">
      <h1>Use our songs in your videos</h1>
      <p>All Good Kid music is <span class="highlight">DMCA free</span> and <span class="highlight">Content ID free</span>!</p>
      <p id="instructions">
        That means that you can use it freely without worrying about strikes - even if your channel is monetized. If you want to be super-safe and generate a license for YouTube appeals, enter your YouTube video URL in the form below.
      </p>
    </div>

    <!-- Callouts -->

    <CalloutBox v-if="checkedEligibility && !eligible" warning>
      Looks like your channel is not part of the YouTube Partner Program. Your
      videos <span class="bold">don't need to be cleared</span>, you can just
      use our songs. If you think this is a mistake, please shoot us an email at
      contact@goodkidofficial.com.
    </CalloutBox>

    <CalloutBox
      v-if="serverError"
      message="Something went wrong. Try again in a bit, and if it doesn't work, then please send us an email at contact@goodkidofficial.com."
      error
    />

    <CalloutBox v-if="clientError" :message="clientError" error />

    <CalloutBox v-if="licenseUrl" success>
      Sweet, we are processing your request! The video should be cleared within
      24 hours.
      <div>
        <a :href="licenseUrl" target="_blank" @click="onDownloadLicense"
          >Download your custom license</a
        >
      </div>
    </CalloutBox>

    <!-- Personal Info -->

    <div
      v-if="showDetailsEditor || (eligible && !detailsConfirmed)"
      class="form"
    >
      <TextInput label="Your Name" type="text" v-model="name" />
      <TextInput label="Email" type="email" v-model="email" />
      <TextInput label="Message" type="text" v-model="message" />
      <button
        v-if="showDetailsEditor"
        @click="saveDetails"
        :disabled="!name || !email"
        class="btn"
      >
        {{ submitRequestBtnText }}
      </button>
      <button
        v-else
        @click="submitRequest"
        :disabled="!name || !email || !videoUrl || loading"
        class="btn"
      >
        {{ submitRequestBtnText }}
      </button>
    </div>

    <!-- Video URL form -->
    <div v-else class="form">
      <div>
        <TextInput
          label="Video URL"
          type="text"
          v-model="videoUrl"
          placeholder="http://youtube.com/watch?v=wmN6W6SfBAk"
        />
      </div>
      <div v-if="eligible">
        <button
          v-if="eligible"
          @click="submitRequest"
          :disabled="!videoUrl || loading"
          class="btn"
        >
          {{ submitRequestBtnText }}
        </button>
        <div id="details">
          License will be generated for {{ name }} ({{ email }})
          <span class="linkBtn" @click="onEditPersonalDetailsClick">edit</span>
        </div>
      </div>
      <button
        v-else
        @click="checkEligibility"
        :disabled="!videoUrl || loading"
        class="btn"
      >
        {{ confirmBtnText }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import CalloutBox from "./CalloutBox.vue";
import TextInput from "./TextInput.vue";

const ELIGIBILITY_URL =
  "https://us-east1-gk-creators.cloudfunctions.net/check-eligibility";
const SUBMIT_URL =
  "https://us-east1-gk-creators.cloudfunctions.net/create-license";
export default {
  name: "ClearVideo",
  data() {
    return {
      videoUrl: "",
      name: "",
      email: "",
      message: "",
      detailsConfirmed: false,
      checkedEligibility: false,
      licenseUrl: "",
      eligible: false,
      clientError: "",
      serverError: "",
      loading: false,
      showDetailsEditor: false,
    };
  },
  components: {
    CalloutBox,
    TextInput,
  },
  computed: {
    confirmBtnText() {
      return this.loading ? "Validating..." : "Confirm";
    },
    submitRequestBtnText() {
      return this.loading ? "Processing..." : "Submit Request";
    },
  },
  methods: {
    onDownloadLicense() {
      this.$gtag.event("download-license-clicked");
    },
    onEditPersonalDetailsClick() {
      this.detailsConfirmed = false;
      this.showDetailsEditor = true;
    },
    checkEligibility() {
      this.loading = true;
      axios
        .post(ELIGIBILITY_URL, { videoUrl: this.videoUrl })
        .then((r) => {
          this.checkedEligibility = true;
          this.eligible = r.data.monetizable;
          this.clientError = null;
          this.serverError = null;
          this.$gtag.event(
            this.eligible ? "eligibility-confirmed" : "eligibility-rejected"
          );
        })
        .catch((e) => {
          if (e.response && e.response.status < 500) {
            this.clientError = e.response.data;
          } else {
            this.serverError = e.message;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitRequest() {
      this.loading = true;
      this.licenseUrl = null;
      axios
        .post(SUBMIT_URL, {
          videoUrl: this.videoUrl,
          name: this.name,
          email: this.email,
          message: this.message,
        })
        .then((r) => {
          this.licenseUrl = r.data.licenseUrl;
          this.detailsConfirmed = true;
          this.clientError = null;
          this.serverError = null;
          this.videoUrl = "";
          this.$gtag.event("clearance-approved");
        })
        .catch((e) => {
          if (e.response && e.response.status < 500) {
            this.clientError = e.response.data;
          } else {
            this.serverError = e.message;
          }
        })
        .finally(() => {
          this.loading = false;
          this.message = "";
        });
    },
    saveDetails() {
      this.detailsConfirmed = true;
      this.showDetailsEditor = false;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#details {
  padding-left: 15px;
  padding-top: 8px;
  font-size: 0.9em;
  color: #5c5347;
}
.btn {
  width: 100%;
  margin-top: 14px;
  background-color: #6b5290;
  font-weight: 600;
  font-family: "Poppins", sans-serif;
  color: white;
  padding: 14px 20px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95em;
  letter-spacing: 0.02em;
  transition: background-color 0.25s ease, transform 0.15s ease, box-shadow 0.25s ease;
  box-shadow: 0 2px 8px rgba(107, 82, 144, 0.3);
}
.btn:hover {
  background-color: #7c62a3;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(107, 82, 144, 0.4);
}
.btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(107, 82, 144, 0.2);
}
.btn:disabled {
  opacity: 40%;
  cursor: default;
  transform: none;
  box-shadow: none;
}
.bold {
  font-weight: 600;
  text-decoration: underline;
}
.linkBtn {
  cursor: pointer;
  color: #6b5290;
  font-weight: 500;
  transition: color 0.2s ease;
}
.linkBtn:hover {
  text-decoration: underline;
  color: #7c62a3;
}

.highlight {
  color: #6b5290;
  font-weight: 600;
  text-shadow: 0 0 8px rgba(107, 82, 144, 0.4);
}

#instructions {
  padding-bottom: 20px;
}
</style>
